/**
 * Post body rules on the console side. Mirrors the gateway's article sanitizer (sitebuilder/render/sanitize.go articlePolicy)
 * so the editor shows what the site shows, and converts between stored HTML and the editor's form (footnotes).
 * Stored contract: <pre><code class="language-x">, <figure data-embed="URL">, <figure><img><figcaption>, <aside class="callout">,
 * <sup id="ref-1"><a href="#fn-1">1</a></sup> + <li id="fn-1">; h2/h3 ids are added by the gateway.
 */
import { marked } from 'marked';

const TAGS = new Set(['P', 'BR', 'HR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'A', 'UL', 'OL', 'LI', 'H2', 'H3', 'H4', 'H5', 'BLOCKQUOTE', 'CODE', 'PRE', 'MARK', 'SUB', 'SUP', 'SPAN',
    'FIGURE', 'FIGCAPTION', 'IMG', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD', 'CAPTION', 'DL', 'DT', 'DD', 'VIDEO', 'AUDIO', 'ASIDE']);
const GONE = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'META', 'LINK', 'NOSCRIPT', 'FORM', 'SVG', 'HEAD', 'TITLE', 'COLGROUP', 'COL']);
const QUIET_TAGS = new Set(['DIV', 'SECTION', 'ARTICLE', 'MAIN', 'HEADER', 'FOOTER', 'NAV', 'FONT', 'CENTER', 'SMALL', 'BIG', 'LABEL', 'COLGROUP', 'COL', 'HEAD', 'META', 'LINK', 'TITLE']);
const ID_RE = /^[a-z0-9-]{1,64}$/;
const HREF_RE = /^(?:https?:|mailto:|tel:|\/|#|[^:]*$)/i;
const SRC_RE = /^(?:https?:\/\/|\/)/i;
const MEDIA_RE = /^https:\/\/[^\s"<>]{1,1000}$/;

const keepAttr = (el, n, v) => {
    const tag = el.tagName;
    switch (n) {
        case 'href': return tag === 'A' && HREF_RE.test(v.trim());
        case 'target': case 'title': return tag === 'A' || (n === 'title' && tag === 'IMG');
        case 'src': return tag === 'IMG' ? SRC_RE.test(v.trim()) : ['VIDEO', 'AUDIO'].includes(tag) && MEDIA_RE.test(v);
        case 'alt': case 'width': case 'height': return tag === 'IMG';
        case 'colspan': case 'rowspan': return (tag === 'TD' || tag === 'TH') && v !== '1';
        case 'scope': return tag === 'TD' || tag === 'TH';
        case 'id': return ['H2', 'H3', 'H4', 'H5', 'A', 'LI', 'SUP', 'P'].includes(tag) && ID_RE.test(v);
        case 'class':
            if (tag === 'CODE') return /^language-[a-z0-9+#-]{1,30}$/.test(v);
            if (tag === 'ASIDE') return v === 'callout';
            return tag === 'FIGURE' && v === 'gallery';
        case 'data-embed': return tag === 'FIGURE' && MEDIA_RE.test(v);
        case 'data-fn': return tag === 'SUP';
        case 'controls': case 'poster': case 'preload': case 'loop': case 'muted': case 'playsinline': return ['VIDEO', 'AUDIO'].includes(tag);
        default: return false;
    }
};

/**
 * Clean HTML by the site's rules. opts.paste: friendlier for foreign content (h1 → h2, h6 → h5).
 * @returns {{html: string, dropped: string[]}} dropped = what the site would remove, for the warning.
 */
export const sanitizeArticle = (html, { paste = false } = {}) => {
    const doc = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
    const dropped = new Set();
    const rename = (el, tag) => {
        const n = doc.createElement(tag);
        while (el.firstChild) n.appendChild(el.firstChild);
        el.replaceWith(n);
        return n;
    };
    const walk = (node) => {
        [...node.childNodes].forEach((orig) => {
            let ch = orig;
            if (ch.nodeType === 8) { ch.remove(); return; }
            if (ch.nodeType !== 1) return;
            if (paste && ch.tagName === 'H1') ch = rename(ch, 'h2');
            if (paste && ch.tagName === 'H6') ch = rename(ch, 'h5');
            if (ch.tagName === 'DEL' || ch.tagName === 'STRIKE') ch = rename(ch, 's');
            const tag = ch.tagName.toUpperCase();
            if (GONE.has(tag)) {
                if (!QUIET_TAGS.has(tag)) dropped.add(`<${tag.toLowerCase()}>`);
                ch.remove();
                return;
            }
            walk(ch);
            if (!TAGS.has(tag)) {
                if (!QUIET_TAGS.has(tag)) dropped.add(`<${tag.toLowerCase()}>`);
                ch.replaceWith(...ch.childNodes);
                return;
            }
            [...ch.attributes].forEach((a) => {
                const n = a.name.toLowerCase();
                if (keepAttr(ch, n, a.value)) return;
                if (n === 'style' || n.startsWith('on')) { if (!paste) dropped.add(n === 'style' ? 'style' : 'on*'); }
                else if ((n === 'src' || n === 'href') && a.value) dropped.add(`${tag.toLowerCase()} ${n}`);
                ch.removeAttribute(a.name);
            });
            if (tag === 'IMG' && !ch.getAttribute('src')) ch.remove();
        });
    };
    walk(doc.body);
    return { html: doc.body.innerHTML, dropped: [...dropped] };
};

/** Stored HTML → editor HTML: footnotes become <sup data-fn="text"> and the list goes away. */
export const toEditorHtml = (stored) => {
    const { html, dropped } = sanitizeArticle(stored);
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    const notes = {};
    doc.body.querySelectorAll('ol').forEach((ol) => {
        const items = [...ol.children];
        if (!items.length || !items.every((li) => /^fn-\d+$/.test(li.id))) return;
        items.forEach((li) => {
            li.querySelectorAll('a[href^="#ref-"]').forEach((a) => a.remove());
            notes[li.id.slice(3)] = li.textContent.trim();
        });
        ol.remove();
    });
    doc.body.querySelectorAll('sup[id^="ref-"]').forEach((sup) => {
        const n = sup.id.slice(4);
        const fn = doc.createElement('sup');
        fn.setAttribute('data-fn', notes[n] || '');
        sup.replaceWith(fn);
    });
    return { html: doc.body.innerHTML, dropped };
};

/** Editor HTML → stored contract. */
export const toStoredHtml = (editorHtml) => {
    const doc = new DOMParser().parseFromString(`<body>${editorHtml || ''}</body>`, 'text/html');
    const notes = [];
    doc.body.querySelectorAll('sup[data-fn]').forEach((sup) => {
        notes.push(sup.getAttribute('data-fn') || '');
        const i = notes.length;
        const s = doc.createElement('sup');
        s.id = `ref-${i}`;
        const a = doc.createElement('a');
        a.setAttribute('href', `#fn-${i}`);
        a.textContent = String(i);
        s.appendChild(a);
        sup.replaceWith(s);
    });
    if (notes.length) {
        const ol = doc.createElement('ol');
        notes.forEach((text, k) => {
            const li = doc.createElement('li');
            li.id = `fn-${k + 1}`;
            li.textContent = `${text} `;
            const back = doc.createElement('a');
            back.setAttribute('href', `#ref-${k + 1}`);
            back.textContent = '↩';
            li.appendChild(back);
            ol.appendChild(li);
        });
        doc.body.appendChild(ol);
    }
    return sanitizeArticle(doc.body.innerHTML).html.replace(/(<p><\/p>)+$/, '');
};

/** Tags used in a piece of HTML (to notice what the editor could not represent). */
export const tagsIn = (html) => {
    const doc = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
    const out = new Set();
    doc.body.querySelectorAll('*').forEach((e) => out.add(e.tagName.toLowerCase()));
    return out;
};

const MD_RE = /^(#{1,6}\s|\s*[-*+]\s+\S|\s*\d+\.\s+\S|>\s|```|---\s*$|\|.+\|\s*$)|\*\*[^*\n]+\*\*|\[[^\]\n]+\]\(https?:[^)\n]+\)|!\[[^\]\n]*\]\([^)\n]+\)/m;
export const looksLikeMarkdown = (text) => MD_RE.test(text || '');
export const markdownToHtml = (text) => sanitizeArticle(marked.parse(text, { gfm: true, breaks: false }), { paste: true }).html;

/** Plain text of a body (AI requests, previews). */
export const plainText = (html) => {
    const doc = new DOMParser().parseFromString(`<body>${(html || '').replace(/<\/(p|h[2-5]|li|blockquote|pre|tr|figcaption)>/gi, '$&\n')}</body>`, 'text/html');
    return doc.body.textContent.replace(/\n{3,}/g, '\n\n').trim();
};

/** Latin words + CJK characters: the same count as the gateway's post.word_count. */
export const countWords = (text) => {
    const t = text || '';
    const cjk = (t.match(/[぀-ヿ㐀-鿿豈-﫿가-힯]/g) || []).length;
    const rest = t.replace(/[぀-ヿ㐀-鿿豈-﫿가-힯]/g, ' ');
    return cjk + (rest.match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu) || []).length;
};

const EMBEDS = [
    ['YouTube', /^https:\/\/(www\.|m\.)?(youtube\.com\/(watch\?|shorts\/|embed\/)|youtube-nocookie\.com\/embed\/|youtu\.be\/)/i],
    ['Vimeo', /^https:\/\/(www\.|player\.)?vimeo\.com\/.+/i],
    ['Spotify', /^https:\/\/open\.spotify\.com\/.+/i],
    ['Bilibili', /^https:\/\/(www\.|player\.)?(bilibili\.com\/|b23\.tv\/).+/i],
    ['Google Maps', /^https:\/\/(www\.)?(google\.[a-z.]+\/maps|maps\.google\.[a-z.]+\/|maps\.app\.goo\.gl\/).*/i],
    ['X', /^https:\/\/(www\.|mobile\.)?(x|twitter)\.com\/[^/]+\/status\/\d+/i],
];
export const embedProvider = (url) => (EMBEDS.find(([, re]) => re.test((url || '').trim())) || [null])[0];

export const CODE_LANGUAGES = ['plaintext', 'bash', 'c', 'cpp', 'csharp', 'css', 'diff', 'go', 'html', 'java', 'javascript', 'json', 'kotlin', 'markdown', 'php', 'python', 'ruby', 'rust', 'sql', 'swift', 'typescript', 'xml', 'yaml'];
